/**
 * The landing's hand-drawn doodles, for the front door (vestra-v3 pages/onboarding.html and
 * pages/waitlist.html). Each one draws itself in once (motion-safe), through the faint wobble of
 * the `fd-ink` filter that InkDefs renders. Strokes are the doodle ink; fills are the landing's
 * gold and green. When it starts drawing is the caller's `[--d:0.95s]` class.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Rendered once per page: the wobble every doodle draws through. */
export function InkDefs() {
  return (
    <svg
      width="0"
      height="0"
      className="absolute"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id="fd-ink" x="-15%" y="-15%" width="130%" height="130%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency=".045"
            numOctaves={2}
            seed={4}
            result="n"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="n"
            scale={2.2}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>
  );
}

/** A drawn stroke: dashes from 1 to 0 over its own length (pathLength=1). */
const STROKE =
  "fill-none stroke-current [stroke-width:2.6] [stroke-linecap:round] [stroke-linejoin:round] motion-safe:[stroke-dasharray:1] motion-safe:[stroke-dashoffset:1] motion-safe:animate-fd-ink motion-safe:[animation-delay:var(--d)]";
/** A fill that arrives just after its outline. */
const FILL =
  "motion-safe:opacity-0 motion-safe:animate-fd-fill motion-safe:[animation-delay:calc(var(--d)+0.25s)]";

function Doodle({
  viewBox,
  className,
  children,
}: {
  viewBox: string;
  className: string;
  children: ReactNode;
}) {
  return (
    <svg
      viewBox={viewBox}
      className={cn(
        "pointer-events-none absolute overflow-visible text-fd-dz-ink",
        className
      )}
      aria-hidden="true"
      focusable="false"
    >
      <g filter="url(#fd-ink)">{children}</g>
    </svg>
  );
}

/** `className` places it and sets its start, e.g. "[--d:0.95s] -left-[.78em] ...". */
type DoodleProps = { className: string };

/** Three pop lines, before a headline's first word. */
export function Pop({ className }: DoodleProps) {
  return (
    <Doodle viewBox="0 0 40 40" className={className}>
      <path
        className={STROKE}
        pathLength={1}
        d="M8 34L2 26M15 28L13 17M22 30L29 21"
      />
    </Doodle>
  );
}

/** A gold four-point spark with a small one beside it. */
export function Spark({ className }: DoodleProps) {
  const big =
    "M24 10C25.5 22 28 27.5 44 30C28 32.5 25.5 38 24 50C22.5 38 20 32.5 4 30C20 27.5 22.5 22 24 10Z";
  return (
    <Doodle viewBox="0 0 60 54" className={className}>
      <path
        className={cn("fill-fd-dz-gold", FILL)}
        transform="translate(3 3)"
        d={big}
      />
      <path className={STROKE} pathLength={1} d={big} />
      <path
        className={STROKE}
        pathLength={1}
        d="M48 3C48.7 8.4 49.8 10.9 57 12C49.8 13.1 48.7 15.6 48 21C47.3 15.6 46.2 13.1 39 12C46.2 10.9 47.3 8.4 48 3Z"
      />
    </Doodle>
  );
}

/** The landing's sun: a gold disc and its rays. */
export function Sun({ className }: DoodleProps) {
  return (
    <Doodle viewBox="0 0 64 64" className={className}>
      <circle
        className={cn("fill-fd-dz-gold", FILL)}
        cx="34.5"
        cy="34.5"
        r="11.5"
      />
      <circle className={STROKE} pathLength={1} cx="32" cy="32" r="11.5" />
      <path
        className={STROKE}
        pathLength={1}
        d="M31.85 3.82L32 15.2L40.01 7.11M53.94 14.31L45.13 21.53L56.45 22.75M59.51 38.13L48.38 35.74L54.48 45.35M44.36 57.33L39.29 47.14L35.58 57.9M19.9 57.46L24.71 47.14L13.98 50.95M4.56 38.41L15.62 35.74L5.95 29.73M9.87 14.54L18.87 21.53L17.54 10.22"
      />
    </Doodle>
  );
}

/** An arrow curling in towards a button. */
export function Arrow({ className }: DoodleProps) {
  return (
    <Doodle viewBox="0 0 120 60" className={className}>
      <path
        className={STROKE}
        pathLength={1}
        d="M6 50C20 22 44 10 60 22C70 30 62 42 54 36C46 28 60 14 80 16C94 18 104 28 110 40"
      />
      <path className={STROKE} pathLength={1} d="M110.3 29L110 40L101 33.6" />
    </Doodle>
  );
}

/** The coffee Bash mentions: a gold mug with steam. */
export function Mug({ className }: DoodleProps) {
  const cup = "M10 30H46V56C46 62 42 66 36 66H20C14 66 10 62 10 56Z";
  return (
    <Doodle viewBox="0 0 64 70" className={className}>
      <path
        className={cn("fill-fd-dz-gold", FILL)}
        transform="translate(3 3)"
        d={cup}
      />
      <path className={STROKE} pathLength={1} d={cup} />
      <path className={STROKE} pathLength={1} d="M46 36C57 36 57 52 46 52" />
      <path
        className={STROKE}
        pathLength={1}
        d="M22 22C18 16 26 12 22 6M33 22C29 16 37 12 33 6"
      />
    </Doodle>
  );
}

/** A drawn green tick in a circle, for "You're Booked." */
export function Tick({ className }: DoodleProps) {
  return (
    <Doodle viewBox="0 0 72 72" className={className}>
      <circle className={cn("fill-fd-dz-green", FILL)} cx="38" cy="38" r="25" />
      <circle className={STROKE} pathLength={1} cx="35" cy="35" r="25" />
      <path className={STROKE} pathLength={1} d="M23 36L31.5 44.5L48 26" />
    </Doodle>
  );
}

/** The coral underline under a headline's last word (no filter: a clean hand stroke). */
export function Underline({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 200 18"
      preserveAspectRatio="none"
      className={cn("pointer-events-none absolute overflow-visible", className)}
      aria-hidden="true"
      focusable="false"
    >
      <path
        className="fill-none stroke-fd-dz-coral [stroke-width:4] [stroke-linecap:round] [vector-effect:non-scaling-stroke] motion-safe:[stroke-dasharray:240] motion-safe:[stroke-dashoffset:240] motion-safe:animate-fd-draw motion-safe:[animation-delay:0.5s]"
        d="M3 11C28 4 48 15 76 9S124 3 150 10S184 14 197 7"
      />
    </svg>
  );
}
